import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import type { NostrMetadata } from '@nostrify/nostrify';

import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useActiveProfileTheme } from '@/hooks/useActiveProfileTheme';
import { genUserName } from '@/lib/genUserName';
import { getAvatarShape } from '@/lib/emojiMask';

import { DittoAvatar } from '@/components/DittoAvatar';
import { ProfileThemeOverlay } from '@/components/ProfileThemeOverlay';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, Globe, Zap, Copy, Check, Settings, Sparkles } from 'lucide-react';
import { useState } from 'react';

interface ProfilePageProps {
  pubkey: string;
}

export function ProfilePage({ pubkey }: ProfilePageProps) {
  const author = useAuthor(pubkey);
  const { user } = useCurrentUser();
  const { data: themeColors } = useActiveProfileTheme(pubkey);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const metadata: NostrMetadata | undefined = author.data?.metadata;
  const fullMetadata = author.data?.event ? safeParseJson(author.data.event.content) : undefined;
  const shape = getAvatarShape(fullMetadata);

  const isOwnProfile = user?.pubkey === pubkey;
  const displayName = metadata?.display_name || metadata?.name || genUserName(pubkey);
  const npubStr = useMemo(() => nip19.npubEncode(pubkey), [pubkey]);
  const shortNpub = npubStr.slice(0, 12) + '...' + npubStr.slice(-6);

  useSeoMeta({
    title: `${displayName} — Ditto Profile`,
    description: metadata?.about || `View ${displayName}'s Nostr profile`,
  });

  const copyNpub = () => {
    navigator.clipboard.writeText(npubStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (author.isLoading) {
    return <ProfileSkeleton />;
  }

  return (
    <>
      {/* Apply the profile owner's theme */}
      <ProfileThemeOverlay colors={themeColors} />

      <div className="min-h-screen bg-background">
        {/* Banner */}
        <div className="relative h-56 sm:h-72 md:h-80 overflow-hidden">
          {metadata?.banner ? (
            <img
              src={metadata.banner}
              alt="Profile banner"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/30 via-primary/10 to-background" />
          )}
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />

          {/* Theme indicator */}
          {themeColors && (
            <div className="absolute top-4 right-4">
              <Badge variant="secondary" className="backdrop-blur-md bg-background/60 border-primary/20 gap-1.5">
                <Sparkles className="w-3 h-3 text-primary" />
                Custom Theme
              </Badge>
            </div>
          )}
        </div>

        {/* Profile Header */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-20 relative z-10">
          {/* Avatar */}
          <div className="flex items-end gap-4 mb-4">
            <div className="relative">
              <DittoAvatar
                src={metadata?.picture}
                alt={displayName}
                shape={shape}
                size={120}
                bordered
                className="ring-4 ring-background"
              />
              {shape && (
                <div className="absolute -bottom-1 -right-1 bg-card rounded-full p-1 border border-border shadow-lg">
                  <span className="text-lg">{shape}</span>
                </div>
              )}
            </div>

            {isOwnProfile && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full ml-auto mb-2 gap-2 border-primary/30 hover:bg-primary/10"
                onClick={() => navigate('/settings')}
              >
                <Settings className="w-4 h-4" />
                Edit Profile
              </Button>
            )}
          </div>

          {/* Name & Identity */}
          <div className="space-y-2 mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold font-title tracking-tight text-foreground">
              {displayName}
            </h1>

            {metadata?.name && metadata.name !== displayName && (
              <p className="text-muted-foreground text-sm">@{metadata.name}</p>
            )}

            {/* NIP-05 */}
            {metadata?.nip05 && (
              <Badge variant="secondary" className="gap-1.5 bg-primary/10 text-primary border-primary/20">
                <Check className="w-3 h-3" />
                {metadata.nip05}
              </Badge>
            )}
          </div>

          {/* Bio */}
          {metadata?.about && (
            <Card className="mb-6 border-border/50 bg-card/50 backdrop-blur-sm">
              <CardContent className="pt-6">
                <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">
                  {metadata.about}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {/* npub */}
            <button
              onClick={copyNpub}
              className="flex items-center gap-3 p-3 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 hover:bg-card transition-all group cursor-pointer text-left"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                {copied ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : (
                  <Copy className="w-4 h-4 text-primary" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Public Key</p>
                <p className="text-sm font-mono text-foreground truncate group-hover:text-primary transition-colors">
                  {shortNpub}
                </p>
              </div>
            </button>

            {/* Website */}
            {metadata?.website && (
              <a
                href={metadata.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 hover:bg-card transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Globe className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Website</p>
                  <p className="text-sm text-foreground truncate group-hover:text-primary transition-colors">
                    {metadata.website.replace(/^https?:\/\//, '')}
                  </p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            )}

            {/* Lightning */}
            {metadata?.lud16 && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-card/50 border border-border/50">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Lightning</p>
                  <p className="text-sm text-foreground truncate">
                    {metadata.lud16}
                  </p>
                </div>
              </div>
            )}

            {/* Bot indicator */}
            {metadata?.bot && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-card/50 border border-border/50">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-lg">🤖</span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Account Type</p>
                  <p className="text-sm text-foreground">Automated / Bot</p>
                </div>
              </div>
            )}
          </div>

          <Separator className="mb-6 bg-border/30" />

          {/* Theme colors preview (if user has a custom theme) */}
          {themeColors && (
            <Card className="mb-6 border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Profile Theme</h3>
                </div>
                <div className="flex gap-3">
                  <ThemeColorSwatch label="Background" hsl={themeColors.background} />
                  <ThemeColorSwatch label="Text" hsl={themeColors.text} />
                  <ThemeColorSwatch label="Primary" hsl={themeColors.primary} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          <div className="pb-12 text-center">
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            >
              Vibed with Shakespeare
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

function ThemeColorSwatch({ label, hsl }: { label: string; hsl: string }) {
  return (
    <div className="flex flex-col items-center gap-1.5 flex-1">
      <div
        className="w-full aspect-square rounded-xl border border-border/30"
        style={{ backgroundColor: `hsl(${hsl})` }}
      />
      <span className="text-[10px] text-muted-foreground uppercase tracking-widest">{label}</span>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <Skeleton className="h-72 w-full rounded-none" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-20 relative z-10">
        <div className="flex items-end gap-4 mb-6">
          <Skeleton className="w-[120px] h-[120px] rounded-full" />
        </div>
        <div className="space-y-3 mb-6">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-5 w-40" />
        </div>
        <Skeleton className="h-24 w-full rounded-xl mb-6" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

function safeParseJson(str: string): Record<string, unknown> | undefined {
  try {
    return JSON.parse(str);
  } catch {
    return undefined;
  }
}
